<template>
<div>
  <div class="title-h3 title-bar">구성 정보</div>
  <hr class="mr-b20" />
  <div class="title-h4 title-bar">기본 정보</div>
  <el-row class="mr-b30" type="flex" justify="space-between" :gutter="20">
    <el-col :span="12">
      <div class="list form-list">
        <el-form ref="configForm" :model="formData" label-width="120px">
          <el-form-item label="서비스명">
            <w-text-form :modifiable="modifiable" :target="formData" property="objName">
            </w-text-form>
          </el-form-item>
          <el-form-item label="등록일">
            <w-text-form :modifiable="false" :target="formData" property="registDatetime" use-slot=true>
              <span slot="data"> {{convertDate(formData.registDatetime)}} </span>
            </w-text-form>
          </el-form-item>
          <el-form-item label="변경일">
            <w-text-form :modifiable="false" :target="formData" property="changeDatetime" use-slot=true>
              <span slot="data"> {{convertDate(formData.changeDatetime)}} </span>
            </w-text-form>
          </el-form-item>
        </el-form>
      </div>
    </el-col>
    <el-col :span="12">
      <div class="list form-list">
        <el-form :model="formData" label-width="120px">
          <el-form-item label="설명">
            <w-text-form :modifiable="modifiable" :target="formData" property="serviceDesc" type="textarea">
            </w-text-form>
          </el-form-item>
          <el-form-item label="중요도">
            <w-obj-importance-form-editor :modifiable="modifiable" :target="formData" property="objImportance">
            </w-obj-importance-form-editor>
          </el-form-item>
        </el-form>
      </div>
    </el-col>
  </el-row>
  <el-row>
    <el-table-wrapper :data="tableData" :columns="columns" :column-default="columnDefault" :show-custom-header="true" :show-search-tool="false" style="width:100%;">
    </el-table-wrapper>
  </el-row>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isNil } from 'lodash';

import { columnDefault } from '@/constants/table';

import { getDateToString } from '@/utils/date';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import MsgUtil from '@/utils/message';

// api
import serviceInfoAPI from '@/api/config/service/service-info';

// for edit cmp
import WTextForm from '@/components/formeditor/TextFormEditor';
import WNumberForm from '@/components/formeditor/NumberFormEditor';
import WSelectForm from '@/components/formeditor/SelectFormEditor';
import WCheckgroupForm from '@/components/formeditor/CheckGroupFormEditor';
import WCheckForm from '@/components/formeditor/CheckFormEditor';
import wManagerTreeForm from '@/components/formeditor/MgrTreeSelectEditor';
import WObjImportanceFormEditor from '@/components/formeditor/ObjImportanceFormEditor';

export default {
  name: 'WConfigStatus',
  props: ['nodeParams'],
  components: {
    serviceInfoAPI,
    WTextForm,
    WNumberForm,
    WSelectForm,
    WCheckgroupForm,
    WCheckForm,
    wManagerTreeForm,
    WObjImportanceFormEditor,
  },
  data() {
    return {
      false: false,
      // Reject데이터
      objOriginData: {},
      formData: {},
      tableData: [],
      columnDefault,
      columns: [
        {
          prop: 'elementTypeName',
          label: '구분',
          sortable: true,
          searchable: true,
        },
        {
          prop: 'elementName',
          label: '노드명',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'ipaddress',
          label: 'IP',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'rscKeyNameList',
          label: '구성항목',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['configRules', 'rejectConfigEditing', 'saveConfigEditing']),
    params() {
      return {
        objId: this.nodeParams.objId,
        serviceId: this.nodeParams.objId,
      };
    },
    modifiable() {
      return this.$store.state.config.isConfigEditing;
    },
  },
  watch: {
    params: {
      immediate: true,
      handler() {
        if (this.modifiable) {
          // 대상 변경 시 수정모드 종료
          this.$store.dispatch('setConfigEditing', false);
        }
        if (this.params.objId) {
          this.loadData();
        }
      },
    },
    rejectConfigEditing: {
      immediate: true,
      handler(to) {
        if (to) {
          this.formData = Object.assign({}, this.objOriginData);
          this.$store.dispatch('initRejectConfig');
        }
      },
    },
    saveConfigEditing: {
      immediate: true,
      handler(to) {
        if (to) {
          this.$refs.configForm.validate((valid) => {
            if (valid) {
              this.$confirm(...MsgUtil.getBasicConfirmData(MsgUtil.action.SAVE))
                .then(() => {
                  const formData = this.formData;
                  const infoParams = Object.assign({}, formData);
                  const modifiedData = formData.modified;

                  const updateDate = {
                    serviceId: infoParams.serviceId,
                    objName: infoParams.objName,
                    objImportance: formData.objImportance, // null이면 delete로 판단하므로 값 변경이 없을 경우에도 서버단에 전달한다.
                  };
                  // 수정된값들
                  if (modifiedData && Object.keys(modifiedData).length > 0) {
                    Object.keys(modifiedData).forEach((key) => {
                      if (key === 'basicinfoSubid') {
                        updateDate.protocol = formData.basicinfoSubid;
                      } else {
                        updateDate[key] = formData[key];
                      }
                    });
                  }

                  const target = '구성 정보';
                  const action = MsgUtil.action.SAVE;
                  serviceInfoAPI
                    .updateServiceObjConfig(updateDate)
                    .then((result) => {
                      if (result.data.success) {
                        this.$message(MsgUtil.getResultMsgData(action, target, true));
                        this.$store.dispatch('endConfigEdit');
                        this.loadData();
                      } else {
                        this.$message(MsgUtil.getResultMsgData(action, target, false));
                      }
                    })
                    .catch(() => {
                      this.$message(MsgUtil.getResultMsgData(action, target, true));
                    });
                })
                .catch(() => {
                  this.$store.dispatch('setSaveConfigEditing', false);
                });
            } else {
              this.$store.dispatch('setSaveConfigEditing', false);
            }
          });
        }
      },
    },
  },
  methods: {
    getDateToString,
    loadData() {
      this.objOriginData = {};

      serviceInfoAPI.getServiceConfigInfoBasic(this.params).then((result) => {
        if (result.data.success) {
          const data = result.data.data;

          Object.assign(this.objOriginData, data);
          this.formData = Object.assign({}, this.objOriginData);
          this.$emit('setConfigData', this.formData);
        }
      });

      serviceInfoAPI.getServiceConfigInfoDeviceList(this.params).then((result) => {
        if (result.data.success) {
          const data = result.data.data;
          this.tableData = data;
        }
      });
    },
    convertDate(value) {
      return isNil(value) ? EMPTY_TEXT : getDateToString(value);
    },
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
  },
};
</script>

<style>
.form-list .el-form-item__label {
  text-align: left;
}

.form-header {
  padding-left: 10px;
  height: 25px;
  font-weight: bold;
  font-size: 14px;
  color: $chinagray;
}
</style>



// WEBPACK FOOTER //
// src/views/config/node/service/configstatus/index.vue